import { defineStore } from 'pinia'
import { ref } from 'vue'

type Theme = 'dark' | 'light'
const THEME_KEY = 'vz-theme' // clé de mémorisation dans le navigateur (localStorage)
const BRAND_KEY = 'vz-brand-color' // couleur d'accent du club, mémorisée pour un rendu instantané au rechargement

/** Convertit "#0055A4" (ou "#05A") en triplet "0, 85, 164". null si invalide. */
function hexToRgbTriplet(hex: string): string | null {
  let h = hex.trim().replace('#', '')
  if (h.length === 3) h = h.split('').map((c) => c + c).join('')
  if (!/^[0-9a-fA-F]{6}$/.test(h)) return null
  const r = parseInt(h.slice(0, 2), 16)
  const g = parseInt(h.slice(2, 4), 16)
  const b = parseInt(h.slice(4, 6), 16)
  return `${r}, ${g}, ${b}`
}

export const useUiStore = defineStore('ui', () => {
  const sidebarOpen = ref(false)
  const mobileNavActive = ref('dashboard')

  /* ── Thème sombre / clair ─────────────────────────────────────────────────────
     - 'dark' par défaut (l'absence d'attribut = mode sombre, le nuancier de base).
     - 'light' pose data-theme="light" sur <html> → l'appli bascule sur le nuancier clair.
     - Le choix est mémorisé dans le navigateur (localStorage) et restauré au login. */
  const theme = ref<Theme>('dark')

  function applyTheme(value: Theme) {
    theme.value = value
    const root = document.documentElement
    if (value === 'light') root.setAttribute('data-theme', 'light')
    else root.removeAttribute('data-theme')
    try { localStorage.setItem(THEME_KEY, value) } catch { /* navigation privée : on ignore */ }
  }

  /** Lit le choix mémorisé (sombre par défaut) et l'applique. À appeler au démarrage. */
  function initTheme() {
    let saved: Theme = 'dark'
    try {
      const s = localStorage.getItem(THEME_KEY)
      if (s === 'light' || s === 'dark') saved = s
    } catch { /* ignore */ }
    applyTheme(saved)
    // Restaure aussitôt la couleur du club mémorisée → pas de clignotement au rechargement.
    try {
      const c = localStorage.getItem(BRAND_KEY)
      if (c) applyBrandColor(c, false)
    } catch { /* ignore */ }
  }

  /* ── Couleur d'accent du club ─────────────────────────────────────────────────
     Injecte (ou retire) la couleur du club dans les variables CSS globales.
     - Passer un hex ("#0055A4") → recolore l'accent partout (boutons, liens,
       surbrillances, focus) et la lueur de l'en-tête.
     - Passer null → revient à l'accent MATCHUP d'origine. */
  const brandColor = ref<string | null>(null)

  function applyBrandColor(hex: string | null, persist = true) {
    const root = document.documentElement
    const rgb = hex ? hexToRgbTriplet(hex) : null

    if (hex && rgb) {
      brandColor.value = hex
      root.style.setProperty('--color-accent', hex)
      root.style.setProperty('--color-accent-rgb', rgb)
      root.style.setProperty('--color-accent-subtle', `rgba(${rgb}, 0.12)`)
      root.style.setProperty('--color-border-accent', hex)
      if (persist) { try { localStorage.setItem(BRAND_KEY, hex) } catch { /* ignore */ } }
    } else {
      // Retour au défaut : on efface nos surcharges → les tokens du thème reprennent la main.
      brandColor.value = null
      root.style.removeProperty('--color-accent')
      root.style.removeProperty('--color-accent-rgb')
      root.style.removeProperty('--color-accent-subtle')
      root.style.removeProperty('--color-border-accent')
      if (persist) { try { localStorage.removeItem(BRAND_KEY) } catch { /* ignore */ } }
    }
  }

  /* ── Personnalisation du plan de travail (grille + fond de l'éditeur) ──────────
     Deux couches se superposent :
     1. Les couleurs propres au TEMPLATE ouvert (réglées par l'admin).
     2. La préférence d'AFFICHAGE du client (bascule fond clair / sombre), qui
        prend le dessus et est mémorisée sur son appareil.
     Le rendu final est recalculé par renderCanvas() à chaque changement. */
  const OPACITY_GRID = 0.24 // intensité fixe des points (discrète mais visible)
  const CANVAS_LIGHT_KEY = 'vz-canvas-light' // préférence client mémorisée
  const LIGHT_BG = '#f4f1eb'  // fond crème clair de la bascule
  const LIGHT_GRID = '117, 129, 141' // points gris (#75818d) sur fond clair

  // Dernières couleurs du template appliquées (mémorisées pour recalcul au toggle).
  let templateCanvas: { grid: string | null; bg: string | null } = { grid: null, bg: null }

  function readCanvasLight(): boolean {
    try { return localStorage.getItem(CANVAS_LIGHT_KEY) === '1' } catch { return false }
  }
  const canvasLight = ref<boolean>(readCanvasLight())

  const isValidHex = (s: string) => /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(s.trim())

  /** Recalcule et applique le fond + la grille selon (template + préférence client). */
  function renderCanvas() {
    const root = document.documentElement

    // Bascule client « clair » → prend le dessus sur le template.
    if (canvasLight.value) {
      root.style.setProperty('--color-canvas', LIGHT_BG)
      root.style.setProperty('--color-canvas-grid', `rgba(${LIGHT_GRID}, ${OPACITY_GRID})`)
      return
    }

    // Sinon : couleurs du template (ou défaut si non défini).
    const gridRgb = templateCanvas.grid ? hexToRgbTriplet(templateCanvas.grid) : null
    if (gridRgb) root.style.setProperty('--color-canvas-grid', `rgba(${gridRgb}, ${OPACITY_GRID})`)
    else root.style.removeProperty('--color-canvas-grid')

    const bg = templateCanvas.bg && isValidHex(templateCanvas.bg) ? templateCanvas.bg.trim() : null
    if (bg) root.style.setProperty('--color-canvas', bg)
    else root.style.removeProperty('--color-canvas')
  }

  /** Enregistre les couleurs du template ouvert et applique le rendu. */
  function applyCanvasStyle(opts: { grid?: string | null; bg?: string | null }) {
    templateCanvas = { grid: opts.grid ?? null, bg: opts.bg ?? null }
    renderCanvas()
  }

  /** Bascule d'affichage client (fond clair / sombre), mémorisée par appareil. */
  function setCanvasLight(value: boolean, persist = true) {
    canvasLight.value = value
    if (persist) { try { localStorage.setItem(CANVAS_LIGHT_KEY, value ? '1' : '0') } catch { /* ignore */ } }
    renderCanvas()
  }
  function toggleCanvasLight() {
    setCanvasLight(!canvasLight.value)
  }

  /** Efface les surcharges CSS (retour au défaut de l'appli). À la sortie de
     l'éditeur : le reste de l'appli ne doit pas hériter du fond du template.
     La préférence client (canvasLight) reste mémorisée pour la prochaine fois. */
  function resetCanvasStyle() {
    templateCanvas = { grid: null, bg: null }
    const root = document.documentElement
    root.style.removeProperty('--color-canvas')
    root.style.removeProperty('--color-canvas-grid')
  }

  function setTheme(value: Theme) {
    applyTheme(value)
  }

  function toggleTheme() {
    applyTheme(theme.value === 'dark' ? 'light' : 'dark')
  }
  // Compteur incrémenté à chaque clic sur « + Nouveau » (header).
  // L'éditeur l'observe pour relancer un nouveau visuel, même si l'on est déjà
  // sur la page /editor (où une simple navigation ne rechargerait rien).
  const newVisualNonce = ref(0)

  function toggleSidebar() {
    sidebarOpen.value = !sidebarOpen.value
  }

  function openSidebar() {
    sidebarOpen.value = true
  }

  function closeSidebar() {
    sidebarOpen.value = false
  }

  function setMobileNav(tab: string) {
    mobileNavActive.value = tab
  }

  function requestNewVisual() {
    newVisualNonce.value++
  }

  return { sidebarOpen, mobileNavActive, newVisualNonce, theme, brandColor, canvasLight, initTheme, setTheme, toggleTheme, applyBrandColor, applyCanvasStyle, resetCanvasStyle, setCanvasLight, toggleCanvasLight, toggleSidebar, openSidebar, closeSidebar, setMobileNav, requestNewVisual }
})
